import { io, type ManagerOptions, type Socket, type SocketOptions } from 'socket.io-client';

import { refreshTokens, tokenManager } from './api';

/**
 * Runtime override fetched from the backend (`GET /settings/public-config`).
 * Set once at app boot via {@link setRuntimeSocketUrl} so the deploy-specific
 * socket host lives in the DB instead of a build-time env var.
 */
let runtimeSocketUrl: string | null = null;

export function setRuntimeSocketUrl(url: string | null | undefined): void {
  runtimeSocketUrl = url && url.trim() ? url.trim() : null;
}

/**
 * Resolves the Socket.IO server URL.
 *
 * Priority:
 *  1. Runtime DB config (`socket_url` feature flag) fetched at boot
 *  2. `VITE_SOCKET_URL` (build-time override)
 *  3. `VITE_API_URL` stripped of trailing `/api` (REST + WS share host)
 *  4. `'/'` — same-origin, requires nginx to proxy `/socket.io/*`
 */
function resolveSocketUrl(): string {
  if (runtimeSocketUrl) return runtimeSocketUrl;

  const explicit = import.meta.env.VITE_SOCKET_URL;
  if (explicit) return explicit;

  const apiUrl = import.meta.env.VITE_API_URL;
  if (apiUrl && /^https?:\/\//i.test(apiUrl)) {
    return apiUrl.replace(/\/?api\/?$/i, '');
  }

  return '/';
}

/** Handshake rejections from the server's JWT middleware (see api socket.ts). */
const AUTH_ERROR_MESSAGES = new Set(['Invalid token', 'Authentication required']);

// Module-level throttle shared by ALL app sockets: several components hold
// their own socket, and on token expiry every one of them fires connect_error
// in the same instant. One refresh fixes them all (the auth function below
// reads the fresh token on the next attempt), so further refresh calls inside
// the window are pointless churn against the rotating refresh token.
const AUTH_REFRESH_MIN_INTERVAL_MS = 10_000;
let lastAuthRefreshAt = 0;

export function createAppSocket(
  token: string,
  options: Partial<ManagerOptions & SocketOptions> = {}
): Socket {
  const socket = io(resolveSocketUrl(), {
    // Function form: evaluated on EVERY connection attempt, so reconnections
    // always present the current access token. A static `{ token }` freezes
    // the token captured at mount — after it expires (15 min) any socket drop
    // becomes permanent: reconnects fail forever with 'Invalid token' while
    // REST keeps working, and socket-only actions (e.g. the pro's offline
    // toggle) become impossible until a full page reload.
    auth: (cb) => cb({ token: tokenManager.getAccessToken() ?? token }),
    transports: ['websocket', 'polling'],
    ...options,
  });

  // Expired access token at handshake time: refresh the pair, then retry (the
  // auth function above picks up the new token). socket.io's own backoff keeps
  // retrying anyway, so a failed/throttled refresh just falls back to the
  // normal reconnection loop; we only force an immediate connect() when no
  // automatic reconnection is scheduled (socket.active covers that).
  socket.on('connect_error', (err: Error) => {
    if (!AUTH_ERROR_MESSAGES.has(err?.message)) return;
    const now = Date.now();
    if (now - lastAuthRefreshAt < AUTH_REFRESH_MIN_INTERVAL_MS) return;
    lastAuthRefreshAt = now;
    void refreshTokens().then((fresh) => {
      if (fresh && !socket.connected && !socket.active) socket.connect();
    });
  });

  return socket;
}
